import { useContext, useState } from 'react';
import { observer } from 'mobx-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import i18nContext from 'components/i18n-context';
import { getErrorMessageForAlert } from 'services/utils';
import { createBlurValidator } from 'services/formValidation';
import { getRegistrationPasswordValidation } from 'components/utils';
import emailRegistrationStore from 'stores/emailRegistrationStore';
import Alert from 'uikit/Alert/Alert';
import Button from 'uikit/Button/Button';
import PasswordInput from 'uikit/PasswordInput';
import Loader from 'components/common/Loader';

const logoPath = process.env.REACT_APP_LOGO_PATH;

// TRN-6922 Stage 4 — real design + logic, see docs/specs/TRN-6922_email_registration/spec.md §8.4. Reuses
// uikit/PasswordInput (same validation criteria, special-symbols set, and check-circle icon as the legacy
// Registration/User/Credentials.js form) — only reskinned via Registration.scss's page-scoped overrides, per
// the user's instruction to base this on the old registration's password input rather than rebuild it.
const PasswordStep = () => {
  const i18n = useContext(i18nContext);
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);

  const { error: storeError, fieldErrors, isLoading } = emailRegistrationStore;

  const form = useFormik({
    validateOnChange: false,
    initialValues: { password: '' },
    validationSchema: Yup.object({
      password: getRegistrationPasswordValidation()
    }),
    onSubmit: async (values) => {
      // Belt-and-suspenders per CLAUDE.md, same reasoning as EmailStep.js's onSubmit.
      if (isLoading) return;

      // Sent as typed: PasswordInput's "No spaces" criterion keeps the button disabled while there is whitespace,
      // so stripping it here would only turn a rejected password into a different, shorter one.
      await emailRegistrationStore.complete(values.password);
    }
  });

  const { values, errors, handleSubmit, handleChange, submitCount } = form;

  const validateOnBlur = createBlurValidator(form);

  return (
    <section className={'registration-form password-step'}>
      {/* TRN-7468: same mobile-only logo as EmailStep — the brand panel with its own logo is hidden on mobile. */}
      <a
        href={process.env.REACT_APP_LINK_HOME_PAGE}
        target={'_blank'}
        rel={'noreferrer'}
        className={'registration-form__mobile-logo'}
      >
        <img src={logoPath} alt={'Transferra'} />
      </a>

      <div className={'registration-form__body'}>
        <div className={'registration-form__heading'}>
          <h1 className={'registration-form__title'}>{i18n.getMessage('registration.password.title')}</h1>
          <p className={'registration-form__step-subtitle'}>{i18n.getMessage('registration.password.subtitle')}</p>
        </div>

        <Alert type={'warning'} message={storeError && getErrorMessageForAlert(i18n, storeError)} />

        <form className={'registration-form__password-form'} onSubmit={handleSubmit} autoComplete={'off'}>
          <PasswordInput
            isRequired={true}
            name={'password'}
            value={values.password}
            onChange={handleChange}
            onBlur={validateOnBlur('password')}
            setIsPasswordVerified={setIsPasswordVerified}
            initialStatus={submitCount}
            label={i18n.getMessage('login.label.password')}
            placeholder={i18n.getMessage('registration.password.placeholder')}
            autoComplete={'new-password'}
            isDisabled={isLoading}
            error={errors?.password || fieldErrors?.password}
            isApiError={!!storeError}
          />

          {/* Per CLAUDE.md (disable in-flight-request buttons): isDisabled is driven by isLoading, not just
              form validity — same as EmailStep.js's submit button. Also gated on isPasswordVerified so the
              button only enables once every requirement is met, matching Figma's disabled/enabled states. */}
          <Button
            type={'primary'}
            roleType={'submit'}
            size={'large'}
            fullWidth={true}
            onClick={() => {}}
            isDisabled={isLoading || !isPasswordVerified}
          >
            {isLoading ? <Loader /> : i18n.getMessage('registration.email.button')}
          </Button>
        </form>
      </div>
    </section>
  );
};

export default observer(PasswordStep);
